import { Component, Input, Output, EventEmitter, signal, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../shared/material/material.module';
import { EnumDescriptionPipe } from '../../shared/pipes/enum-description.pipe';
import { AvailabilityComponent } from '../../shared/availability/availability.component';
import { PageEvent } from '@angular/material/paginator';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ResourceExamesEnum, ResourceTerapiaEnum } from '../../shared/enums';
import { DialogRegisterNegotiationComponent } from '../../shared/dialogs/dialog-register-negotiation/dialog-register-negotiation.component';
import { MatDialog } from '@angular/material/dialog';
import { ViewModeService } from '../services/view-mode.service';
import { ComparisonService } from '../services/comparison.service';
import { ComparisonDialogComponent } from '../comparison-dialog/comparison-dialog.component';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';

interface ResourceItem {
  key: string;
  description: string;
}

@Component({
  selector: 'app-advanced-search-resource-results',
  standalone: true,
  templateUrl: './advanced-search-resource-results.component.html',
  styleUrls: ['./advanced-search-resource-results.component.scss'],
  imports: [CommonModule, MaterialModule, EnumDescriptionPipe, AvailabilityComponent]
})
export class AdvancedSearchResourceResultsComponent {
  dialog = inject(MatDialog);
  viewModeService = inject(ViewModeService);
  comparisonService = inject(ComparisonService);

  // Computed signal para o modo de visualização
  viewMode = computed(() => this.viewModeService.viewMode());
  
  // Computed signals para comparação
  comparisonCount = computed(() => this.comparisonService.count());
  canCompare = computed(() => this.comparisonService.canCompare());
  isFull = computed(() => this.comparisonService.isFull());

  @Input() items: any[] = [];
  @Input() isLoading = false;
  @Input() pageSize: number = 10;
  @Input() pageSizeOptions: number[] = [10, 25, 50];
  @Input() length = 0; // total length when server-side pagination
  @Input() serverSide = false;
  @Input() searchConfig: any;

  @Output() page = new EventEmitter<PageEvent>();
  readonly panelOpenState = signal(false);

  pageIndex = 0;

  get pagedItems(): any[] {
    if (this.serverSide) {
      return this.items;
    }
    const start = this.pageIndex * this.pageSize;
    return this.items ? this.items.slice(start, start + this.pageSize) : [];
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.page.emit(event);
  }

  toggleViewMode(): void {
    this.viewModeService.toggleViewMode();
  }
  
  isItemSelected(itemId: string): boolean {
    return this.comparisonService.isSelected(itemId);
  }
  
  toggleItemSelection(item: any, event: MatCheckboxChange): void {
    const success = this.comparisonService.toggleItem({
      id: item.id,
      type: 'resource',
      data: item
    });
    
    // Se não conseguiu adicionar (limite atingido), mostra mensagem
    if (!success && !this.isItemSelected(item.id)) {
      // Item não foi adicionado porque o limite foi atingido
      alert(`Você pode comparar no máximo ${this.comparisonService.getMaxItems()} itens.`);
    }
  }
  
  openComparison(): void {
    const items = this.comparisonService.items();
    
    this.dialog.open(ComparisonDialogComponent, {
      data: { items },
      width: '90vw',
      maxWidth: '1200px',
      maxHeight: '90vh',
      panelClass: 'comparison-dialog-panel'
    });
  }
  
  clearComparison(): void {
    this.comparisonService.clear();
  }

  openExportDialog(): void {
    this.dialog.open(ExportDialogComponent, {
      data: {
        searchCriteria: this.searchConfig,
        results: this.items,
        searchType: 'resource'
      },
      width: '600px',
      maxWidth: '90vw',
      disableClose: false,
      panelClass: 'export-dialog-panel'
    });
  }

  // Helper methods para organização e formatação dos exames e terapias
  getResourceGroups(resources: string[] | undefined): { exames: ResourceItem[], terapias: ResourceItem[] } {
    if (!resources) return { exames: [], terapias: [] };

    const exames: ResourceItem[] = [];
    const terapias: ResourceItem[] = [];

    resources.forEach(resource => {
      // Verifica se é um exame
      const exameDesc = this.getExameDescription(resource);
      if (exameDesc) {
        exames.push({ key: resource, description: exameDesc });
        return;
      }


      // Verifica se é uma terapia
      const terapiaDesc = this.getTerapiaDescription(resource);
      if (terapiaDesc) {
        terapias.push({ key: resource, description: terapiaDesc });
        return;
      }

      // Se não encontrou em nenhum dos enums, usa o valor original
      // Tenta determinar se é exame, terapia ou terapia a distância com base em regras de negócio
      if (this.isTerapia(resource)) {
        terapias.push({ key: resource, description: resource });
      } else {
        exames.push({ key: resource, description: resource });
      }
    });

    return {
      exames: exames.sort((a, b) => a.description.localeCompare(b.description)),
      terapias: terapias.sort((a, b) => a.description.localeCompare(b.description))
    };
  }

  getExameDescription(resourceKey: string): string | null {
    return (ResourceExamesEnum as any)[resourceKey] || null;
  }

  getResourcesDescription(resources: string[] | undefined): string {
    if (!resources || resources.length === 0) return '';

    return resources
      .map(res => this.description(res))
      .join(', ');
  }

  description(resourceKey: string): string {
    // Tenta buscar nos enums de exames
    const exameDesc = this.getExameDescription(resourceKey);
    if (exameDesc) {
      return exameDesc;
    }

    // Tenta buscar nos enums de terapias
    const terapiaDesc = this.getTerapiaDescription(resourceKey);
    if (terapiaDesc) {
      return terapiaDesc;
    }

    // Se não encontrar em nenhum enum, retorna a própria chave
    return resourceKey;
  }

  getTerapiaDescription(resourceKey: string): string | null {
    return (ResourceTerapiaEnum as any)[resourceKey] || null;
  }

  // Determina se um exames e terapias é terapia baseado em regras de negócio
  isTerapia(resourceKey: string): boolean {
    const terapiaKeys = ['HEMODIALISE', 'QUIMIOTERAPIA', 'RADIOTERAPIA'];
    return terapiaKeys.includes(resourceKey);
  }

  createRegisterNegotiation(id: any) {
    const dialogRef = this.dialog.open(DialogRegisterNegotiationComponent, {
      data: {
        initiatorCpfCnpj: this.searchConfig.cpfCnpj,
        referenceId: id,
        referenceType: this.searchConfig.type === 'OFFERING' ? 'serviceOffering' : 'serviceDemand'
      },
    });

    dialogRef.afterClosed().subscribe();
  }

}
